import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"
import { Portuguese } from "flatpickr/dist/l10n/pt"
import { createClearButton } from "../helpers/clear_button"

export default class extends Controller {
  connect() {
    const locale = document.documentElement.lang
    const mode = this.element.dataset.mode || "single"

    const localeConfig = locale === "pt-BR" 
      ? { 
          ...Portuguese, 
          rangeSeparator: " - ",
          weekdays: {
            shorthand: ["D", "S", "T", "Q", "Q", "S", "S"],
            longhand: Portuguese.weekdays.longhand
          }
        } 
      : "default"

    let config = {
      locale: localeConfig,
      dateFormat: "Y-m-d",
      altInput: true,
      altFormat: locale === "pt-BR" ? "d/m/Y" : "m/d/Y",
      allowInput: true,
      mode: mode,
    };

    if (mode === "range") {
      config = {
        ...config,
        showMonths: window.innerWidth < 768 ? 1 : 2,
        closeOnSelect: false,
        onReady: (selectedDates, dateStr, instance) => {
          this.buildCustomUI(instance);
        },
        onChange: (selectedDates, dateStr, instance) => {
          this.updateClearButton(instance);
        },
        onClose: (selectedDates, dateStr, instance) => {
          this.updateClearButton(instance);
        }
      }
    }

    this.picker = flatpickr(this.element, config)

    if (mode === "single" && this.picker.altInput) {
      this.picker.altInput.addEventListener("input", (e) => {
        this.autoFormatDate(e)
      })
    }

    if (mode === "range" && this.element.value) {
      setTimeout(() => this.updateClearButton(this.picker), 0)
    }
  }

  autoFormatDate(event) {
    const input = event.target
    const raw = input.value
    const digits = raw.replace(/\D/g, "").slice(0, 8)

    if (digits.length <= 2) return

    let formatted = digits.slice(0, 2)
    if (digits.length > 2) formatted += "/" + digits.slice(2, 4)
    if (digits.length > 4) formatted += "/" + digits.slice(4)

    if (formatted !== raw) {
      const cursorPos = formatted.length
      input.value = formatted
      input.setSelectionRange(cursorPos, cursorPos)
    }
  }

  buildCustomUI(instance) {
    if (instance.calendarContainer.classList.contains('custom-range-picker')) return;
    
    instance.calendarContainer.classList.add('custom-range-picker')
    
    const mainWrapper = document.createElement('div')
    mainWrapper.className = 'w-full flex flex-col md:flex-row'
    
    // Sidebar
    const sidebar = document.createElement('div')
    sidebar.className = 'flatpickr-sidebar'

    const ds = this.element.dataset

    const ranges = [
      { label: ds.labelToday, getDates: () => [new Date(), new Date()] },
      {
        label: ds.labelYesterday,
        getDates: () => {
          const start = new Date(new Date().setDate(new Date().getDate() - 1))
          const end = new Date(new Date().setDate(new Date().getDate() - 1))
          return [start, end]
        }
      },
      { 
        label: ds.labelLast7,
        getDates: () => {
          const start = new Date(new Date().setDate(new Date().getDate() - 6))
          const end = new Date()
          return [start, end]
        }
      },
      { 
        label: ds.labelLast30,
        getDates: () => {
          const start = new Date(new Date().setDate(new Date().getDate() - 29))
          const end = new Date()
          return [start, end]
        }
      },
      { 
        label: ds.labelThisMonth,
        getDates: () => {
          const d = new Date()
          const start = new Date(d.getFullYear(), d.getMonth(), 1)
          const end = new Date(d.getFullYear(), d.getMonth() + 1, 0)
          return [start, end]
        }
      },
      { 
        label: ds.labelLastMonth,
        getDates: () => {
          const d = new Date()
          const start = new Date(d.getFullYear(), d.getMonth() - 1, 1)
          const end = new Date(d.getFullYear(), d.getMonth(), 0)
          return [start, end]
        }
      }
    ]

    const buttons = [];
    this._sidebarButtons = buttons;

    ranges.forEach((range) => {
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.className = 'flatpickr-sidebar-btn'
      btn.innerText = range.label
      btn.addEventListener('click', () => {
        buttons.forEach(b => b.classList.remove('active'))
        btn.classList.add('active')
        instance.setDate(range.getDates(), false)
      })
      buttons.push(btn);
      sidebar.appendChild(btn)
    })

    // Fix arrow colors (flatpickr injects fill attributes directly on SVG elements)
    this._fixArrows = () => {
      // Hide year
      instance.calendarContainer.querySelectorAll('.numInputWrapper').forEach(el => {
        el.style.display = 'none'
      })
      // Remove fill attributes set by flatpickr JS and force currentColor
      instance.calendarContainer.querySelectorAll(
        '.flatpickr-prev-month svg, .flatpickr-prev-month path, .flatpickr-next-month svg, .flatpickr-next-month path'
      ).forEach(el => {
        el.removeAttribute('fill')
        el.setAttribute('fill', 'currentColor')
        el.style.cssText = 'fill: currentColor !important'
      })
    }

    this._fixArrows()
    instance.config.onMonthChange = [() => setTimeout(this._fixArrows, 0)]
    instance.config.onYearChange  = [() => setTimeout(this._fixArrows, 0)]

    // Intercept flatpickr's hover handler that sets fill="#f64747"
    // Use a MutationObserver to catch any attribute changes on arrow SVGs
    const observer = new MutationObserver(() => {
      instance.calendarContainer.querySelectorAll(
        '.flatpickr-prev-month svg, .flatpickr-prev-month path, .flatpickr-next-month svg, .flatpickr-next-month path'
      ).forEach(el => {
        if (el.getAttribute('fill') && el.getAttribute('fill') !== 'currentColor') {
          el.setAttribute('fill', 'currentColor')
          el.style.cssText = 'fill: currentColor !important'
        }
      })
    })
    observer.observe(instance.calendarContainer, { subtree: true, attributes: true, attributeFilter: ['fill'] })
    this._arrowObserver = observer

    // Action Bar
    const actionBar = document.createElement('div')
    actionBar.className = 'flatpickr-action-bar justify-end'

    const cancelBtn = document.createElement('button')
    cancelBtn.type = 'button'
    cancelBtn.className = 'btn btn-text btn-sm text-primary-600'
    cancelBtn.innerText = ds.labelCancel
    cancelBtn.addEventListener('click', () => instance.close())

    const applyBtn = document.createElement('button')
    applyBtn.type = 'button'
    applyBtn.className = 'btn btn-primary btn-sm'
    applyBtn.innerText = ds.labelApply
    applyBtn.addEventListener('click', () => {
      const selected = instance.selectedDates;
      if (selected && selected.length === 2) {
        instance.setDate(selected, true);
        this.element.dispatchEvent(new Event("change", { bubbles: true }));
      }
      instance.close();
    })

    actionBar.appendChild(cancelBtn)
    actionBar.appendChild(applyBtn)

    // Assemble
    const innerWrapper = document.createElement('div')
    innerWrapper.className = 'flatpickr-inner-wrapper'
    
    while(instance.calendarContainer.firstChild) {
       innerWrapper.appendChild(instance.calendarContainer.firstChild)
    }
    
    mainWrapper.appendChild(sidebar)
    mainWrapper.appendChild(innerWrapper)
    
    instance.calendarContainer.appendChild(mainWrapper)
    instance.calendarContainer.appendChild(actionBar)
  }

  // Injects/removes the clear X button next to the altInput
  updateClearButton(instance) {
    const altInput = instance.altInput
    if (!altInput) return

    const wrapper = altInput.parentElement
    if (!wrapper) return

    // Remove existing clear btn
    const existing = wrapper.querySelector('.datepicker-clear-btn')
    if (existing) existing.remove()

    // Only show if dates are selected
    if (!instance.selectedDates || instance.selectedDates.length === 0) {
      altInput.style.paddingRight = ''
      return
    }

    // Add padding so text doesn't overlap the X
    altInput.style.paddingRight = '2rem'

    const ds = this.element.dataset

    const clearBtn = createClearButton({
      title: ds.labelClear,
      onClear: (e) => {
        e.preventDefault()
        e.stopPropagation()
        instance.clear()
        clearBtn.remove()
        altInput.style.paddingRight = ''

        instance.element.dispatchEvent(new Event('change', { bubbles: true }))
        instance.element.dispatchEvent(new Event('input', { bubbles: true }))
        const form = instance.element.closest('form')
        if (form) form.requestSubmit()
      }
    })

    // Position modifications for this specific context
    clearBtn.classList.add('datepicker-clear-btn', 'absolute', 'right-[6px]', 'z-30')
    clearBtn.style.position = 'absolute'
    clearBtn.style.right = '6px'
    clearBtn.style.zIndex = '30'

    // Make wrapper relative if it isn't already
    wrapper.style.position = 'relative'

    wrapper.appendChild(clearBtn)
  }

  disconnect() {
    if (this._arrowObserver) this._arrowObserver.disconnect()
    if (this.picker) this.picker.destroy()
  }
}
